import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, BellRing, Camera } from 'lucide-react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Controlled dialog that renders the EXACT email a guest receives (reminder or
 * follow-up), fetched from the backend so the host gets a pixel-accurate preview.
 */
export const ReminderPreviewDialog = ({
  event, kind = 'reminder', note = '', photoIds = [], open, onOpenChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const isFollowup = kind === 'followup';
  const photoKey = (photoIds || []).join(',');

  useEffect(() => {
    if (!open || !event) return undefined;
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      setData(null);
      try {
        const endpoint = isFollowup ? 'followups/preview' : 'reminders/preview';
        const body = isFollowup ? { note, photo_ids: photoIds || [] } : { note };
        const r = await api.post(`/events/${event.id}/${endpoint}`, body);
        if (active) setData(r.data);
      } catch (e) {
        if (active) toast.error('Could not load preview');
      } finally {
        if (active) setLoading(false);
      }
    }, 0);
    return () => { active = false; clearTimeout(t); };
  }, [open, event, note, photoKey, isFollowup]);

  const subject = data?.subject
    || (event ? (isFollowup ? `Thank you — ${event.title}` : `Reminder: ${event.title}`) : '');
  const Icon = isFollowup ? Camera : BellRing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" aria-describedby="email-preview-desc" data-testid="reminder-preview-dialog">
        <DialogHeader className="px-5 pt-5 pb-4 border-b border-border space-y-2 text-left">
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" /> {isFollowup ? 'Follow-up email preview' : 'Reminder email preview'}
          </DialogTitle>
          <div className="text-sm space-y-0.5">
            <div><span className="text-muted-foreground">From: </span><span className="text-foreground">Thrive Gatherings</span></div>
            <div className="truncate"><span className="text-muted-foreground">Subject: </span><span className="text-foreground font-medium" data-testid="reminder-preview-subject">{subject}</span></div>
          </div>
          <p id="email-preview-desc" className="text-xs text-muted-foreground">This is exactly what guests will see when this email goes out.</p>
        </DialogHeader>
        <div className="bg-[#FBF7F0] h-[66vh] overflow-hidden">
          {loading || !data ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <iframe
              title="Email preview"
              srcDoc={data.html}
              className="w-full h-full border-0 bg-white"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              data-testid="reminder-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
